import type * as React from 'react';
import { cn } from '@wundo/ui';

/**
 * How wide a column of prose and form fields is allowed to run.
 *
 * `form` is the ordinary one: labels, inputs and short paragraphs, at the width
 * a row of them is still read as a row. `editor` is for a page whose subject is
 * a long text — a routine's prompt — where the box being typed into is most of
 * the page and a little more room is worth the longer line.
 */
export type ReadingColumnSize = 'form' | 'editor';

const sizeClass: Record<ReadingColumnSize, string> = {
  form: 'max-w-190',
  editor: 'max-w-256',
};

export interface ReadingColumnProps extends React.ComponentProps<'div'> {
  size?: ReadingColumnSize;
}

/*
 * The reading column inside a console-width page.
 *
 * The shell runs this app's content out to `max-w-console` so lists, tables and
 * dashboards get the monitor they are being read on. A form does not want that:
 * a label at one end of 1600px and its input at the other is not a form. So the
 * pages that are forms put themselves back in a column — this one, in one
 * place, rather than the sixteen literal `max-w-190`s that used to cap every
 * page in the console whether it was a form or not.
 *
 * It caps but never centres: the column starts where the page title starts, so
 * a heading and the fields under it stay on the same left edge. Below the cap
 * it is simply the full width, which is what a phone gets.
 */
export function ReadingColumn({
  size = 'form',
  className,
  ...props
}: ReadingColumnProps) {
  return (
    <div
      data-slot='reading-column'
      className={cn('w-full', sizeClass[size], className)}
      {...props}
    />
  );
}
